import React, { useState, useEffect } from "react";
import axios from "axios";
import { Star, ExternalLink } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const TestimonialsSection = ({ content }) => {
  const cfg = content?.testimonials || { badge: "Avis Google vérifiés", title: "Nos Clients Parlent", subtitle: "" };
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  // Static fallback testimonials (used only if Google API returns none)
  const fallbackTestimonials = [
    {
      author_name: "Laurent M.",
      rating: 5,
      text: "Service impeccable ! Ma voiture n'a jamais aussi bien roulé après la reprogrammation. L'équipe est professionnelle et à l'écoute.",
      relative_time: "",
    },
    {
      author_name: "Sophie D.",
      rating: 5,
      text: "Très satisfaite de l'entretien complet. Prix raisonnables et travail soigné. Je recommande vivement !",
      relative_time: "",
    },
    {
      author_name: "Marc T.",
      rating: 5,
      text: "Changement de pneus rapide et efficace. Bon conseil sur le choix des pneumatiques. Service au top !",
      relative_time: "",
    },
  ];

  useEffect(() => {
    axios.get(`${API}/google-testimonials`)
      .then(r => {
        const list = r.data?.testimonials || [];
        setTestimonials(list.length ? list : fallbackTestimonials);
      })
      .catch(() => setTestimonials(fallbackTestimonials))
      .finally(() => setLoading(false));
  }, []);

  return (
    <section className="py-20 px-4 bg-[#0F0F0F]">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <p className="text-sm font-bold tracking-[0.2em] uppercase text-[#FF3B30] mb-4">
            {cfg.badge}
          </p>
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight uppercase text-white">
            {cfg.title}
          </h2>
          {cfg.subtitle && (
            <p className="text-white/60 text-base mt-4 max-w-2xl mx-auto">{cfg.subtitle}</p>
          )}
        </div>

        {loading ? (
          <div className="text-center text-white/40 text-sm">Chargement des avis…</div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {testimonials.map((t, idx) => (
                <div
                  key={idx}
                  data-testid={`testimonial-${idx}`}
                  className="bg-[#121212] border border-white/10 p-6 rounded-lg hover:border-[#FF3B30]/30 transition-all duration-300 flex flex-col"
                >
                  {/* Header: avatar + name + time */}
                  <div className="flex items-center gap-3 mb-3">
                    {t.author_photo ? (
                      <img
                        src={t.author_photo}
                        alt={t.author_name}
                        referrerPolicy="no-referrer"
                        className="w-10 h-10 rounded-full object-cover border border-white/10"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-[#FF3B30]/20 border border-[#FF3B30]/40 flex items-center justify-center text-white font-bold text-sm">
                        {(t.author_name || "?").slice(0, 1).toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-semibold text-sm truncate">{t.author_name}</p>
                      {t.relative_time && (
                        <p className="text-white/40 text-xs">{t.relative_time}</p>
                      )}
                    </div>
                    <svg viewBox="0 0 24 24" className="w-5 h-5 shrink-0" aria-label="Google">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                  </div>

                  {/* Stars */}
                  <div className="flex items-center gap-1 mb-3">
                    {[...Array(t.rating || 5)].map((_, i) => (
                      <Star key={i} size={14} className="text-yellow-400 fill-yellow-400" />
                    ))}
                  </div>

                  {/* Quote */}
                  <p className="text-white/80 text-sm leading-relaxed italic line-clamp-6">
                    &ldquo;{t.text}&rdquo;
                  </p>
                </div>
              ))}
            </div>

            {/* CTA: Laisser un avis Google */}
            <div className="mt-12 text-center">
              <a
                href="https://search.google.com/local/writereview?placeid=ChIJVRkH-kId90cRrLLZ5N-gVhc"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="leave-google-review-btn"
                className="inline-flex items-center gap-3 bg-white hover:bg-white/90 text-[#0A0A0A] font-semibold px-6 py-3 rounded-full transition-all duration-300 shadow-lg hover:shadow-[0_0_20px_rgba(255,255,255,0.15)]"
              >
                <svg viewBox="0 0 24 24" className="w-5 h-5" aria-label="Google">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <span>Laisser un avis Google</span>
                <Star size={16} className="text-yellow-400 fill-yellow-400" />
              </a>
              <p className="text-white/40 text-xs mt-3">
                Votre retour nous aide à nous améliorer et à mieux vous servir.
              </p>
            </div>
          </>
        )}
      </div>
    </section>
  );
};

